import {
    Box,
    Grid,
    Link,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getPath } from '../../../pages/Pages';
import { formatPriceByLocale, formatPeriod } from '../../../lib/utils';

type Event = {
    id: number;
    name: string;
    sport: {
        name: string;
    };
    sc: {
        id: number;
        name: string;
    };
    ini: string;
    end: string;
    inscIni: string;
    inscEnd: string;
    price: number;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
    teamable: boolean;
    numTeams?: number;
    maxTeams?: number;
    numParticipants?: number;
    maxParticipants?: number;
};

type Props = {
    event?: Event;
};

/**
 * Card representing an event.
 * @param event Event object.
 * @returns {React.ReactElement|null}
 */
export function SttEventCard({ event }: Props): React.ReactElement | null {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    if (!event) {
        return null;
    }

    const now = new Date();
    const inTime =
        isAfter(now, parseISO(event.ini)) && isBefore(now, parseISO(event.end));
    const inIscTime =
        isAfter(now, parseISO(event.inscIni)) &&
        isBefore(now, parseISO(event.inscEnd));
    const dateFormat = mdUp ? 'D MMMM YYYY' : 'DD/MM/YYYY';
    const [period, , periodLabel] = formatPeriod(
        event.ini,
        event.end,
        { dateFormat },
        t,
    );
    const [inscPeriod] = formatPeriod(
        event.inscIni,
        event.inscEnd,
        { dateFormat },
        t,
    );

    return (
        <Box p={3}>
            <Grid container spacing={1}>
                <Grid size={{ xs: 12, md: 10 }}>
                    <div>
                        <Link
                            onClick={() =>
                                navigate(
                                    getPath('eventPublic', {
                                        state: {
                                            id: event.id,
                                        },
                                    }),
                                )
                            }
                        >
                            <Typography variant="h5">{event.name}</Typography>
                        </Link>
                    </div>
                    <div>
                        <Link
                            onClick={() =>
                                navigate(
                                    getPath('timetable', {
                                        state: {
                                            id: event.sc.id,
                                        },
                                    }),
                                )
                            }
                        >
                            <Typography variant="body2">
                                {event.sc.name}
                            </Typography>
                        </Link>
                    </div>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <Box textAlign={mdUp ? 'right' : 'left'}>
                        <Typography variant="button">
                            {event.sport.name}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body1">
                        <span>{periodLabel}: </span>
                        <span className={inTime ? '' : ''}>{period}</span>
                    </Typography>
                    <Typography variant="body1">
                        <span>{t('Inscription')}: </span>
                        <span className={inIscTime ? '' : ''}>
                            {inscPeriod}
                        </span>
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box display="flex" justifyContent="space-between">
                        <Box flex={2}>
                            <Typography variant="body2">
                                {t('Price')}
                            </Typography>
                            <Typography variant="body1">{`${t(
                                'From',
                            )} ${formatPriceByLocale(
                                event.price,
                                event?.priceShape?.currency,
                                event?.priceShape?.locale,
                            )}`}</Typography>
                        </Box>
                        {event.teamable ? (
                            <Box textAlign="right" flex={1}>
                                <Typography variant="body2">
                                    {t('Teams')}
                                </Typography>
                                <Typography variant="body1">{`${
                                    event.numTeams
                                }${
                                    event.maxTeams ? ` / ${event.maxTeams}` : ''
                                }`}</Typography>
                            </Box>
                        ) : (
                            <Box textAlign="right" flex={1}>
                                <Typography variant="body2">
                                    {t('SignedCount')}
                                </Typography>
                                <Typography variant="body1">{`${
                                    event.numParticipants
                                }${
                                    event.maxParticipants
                                        ? ` / ${event.maxParticipants}`
                                        : ''
                                }`}</Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
